import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const HOUR_H = 44; // px per timme
const DAY_MIN = 24 * 60;
const MIN_H = 22;

const minutesOf = (iso) => {
  if (!iso) return 0;
  const [h, m] = iso.slice(11, 16).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Ordern sträcker sig från start- till sluttid (flerdagsjobb klipps per dygn)
const slotFor = (job, key) => {
  const from = job.start?.slice(0, 10);
  const to = (job.end || job.start)?.slice(0, 10);
  const startMin = from === key ? minutesOf(job.start) : 0;
  const endMin = to === key ? Math.max(minutesOf(job.end || job.start), startMin + 30) : DAY_MIN;
  return {
    top: (startMin / 60) * HOUR_H,
    height: Math.max(((endMin - startMin) / 60) * HOUR_H, MIN_H),
  };
};

export const WeekSchedule = ({
  days, selected, todayKey, dayNames, typeBar, dragProps, dayDropProps, dragOver, dragJob,
  onSelect, onOpenJob, onNewJob, leader, staffName,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const [order, setOrder] = useState({});
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

  // Veckovyn öppnas alltid vid 06:00 – användaren kan scrolla fritt därifrån
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 6 * HOUR_H;
  }, []);

  // Ordrar som krockar i tid ritas sida vid sida; först i listan ligger längst fram
  const orderedGroup = (day, job) => {
    const a = slotFor(job, day.key);
    const ids = order[day.key] || day.jobs.map((x) => x.id);
    return day.jobs
      .filter((o) => {
        const b = slotFor(o, day.key);
        return b.top < a.top + a.height && a.top < b.top + b.height;
      })
      .map((o) => o.id)
      .sort((x, y) => ids.indexOf(x) - ids.indexOf(y));
  };

  return (
    <div className="overflow-hidden rounded-lg border border-amber-100 bg-white" data-testid="week-schedule">
      <div className="grid grid-cols-[52px_repeat(7,minmax(0,1fr))] border-b border-amber-100 bg-amber-100/60">
        <div className="px-1 py-2 text-center text-[11px] font-bold uppercase tracking-[0.05em] text-amber-900">
          {t("cal.time")}
        </div>
        {days.map((d) => (
          <button
            key={d.key}
            data-testid={`calendar-day-${d.key}`}
            onClick={() => onSelect(d.key)}
            {...dayDropProps(d.key)}
            className={`relative border-l border-amber-100 px-1 py-1.5 text-center transition-colors duration-200 ${
              dragOver === d.key
                ? "bg-blue-100"
                : d.key === selected
                ? "bg-blue-600 text-white"
                : "hover:bg-amber-100"
            }`}
          >
            {d.holiday && (
              <span
                title={d.holiday}
                data-testid={`holiday-star-${d.key}`}
                className="absolute right-1 top-0.5 text-[11px] leading-none text-red-600"
              >
                ★
              </span>
            )}
            <div className={`text-[11px] font-medium uppercase ${d.key === selected ? "text-blue-100" : "text-slate-600"}`}>
              {dayNames[(d.date.getDay() + 6) % 7]}
            </div>
            <div
              className={`font-heading text-base font-bold ${
                d.key === todayKey && d.key !== selected ? "text-blue-600" : ""
              }`}
            >
              {d.date.getDate()}
            </div>
          </button>
        ))}
      </div>

      <div ref={scrollRef} className="max-h-[560px] overflow-y-auto">
        <div className="grid grid-cols-[52px_repeat(7,minmax(0,1fr))]">
          <div>
            {hours.map((h) => (
              <div
                key={h}
                style={{ height: HOUR_H }}
                className="border-b border-slate-100 pr-1 pt-0.5 text-right text-[10px] font-medium text-slate-400"
              >
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>

          {days.map((d) => (
            <div
              key={d.key}
              {...dayDropProps(d.key)}
              data-testid={`calendar-dropzone-${d.key}`}
              className={`relative border-l border-slate-100 ${
                dragOver === d.key ? "bg-blue-50" : d.key === selected ? "bg-blue-50/40" : ""
              }`}
            >
              {hours.map((h) => (
                <div
                  key={h}
                  style={{ height: HOUR_H }}
                  className="border-b border-slate-100"
                  onDoubleClick={leader ? () => onNewJob(d.key) : undefined}
                />
              ))}

              {d.absences.length > 0 && (
                <div
                  data-testid={`week-absence-${d.key}`}
                  className="absolute inset-x-0.5 top-0 rounded-sm bg-orange-100 px-1 text-[10px] font-semibold text-orange-800"
                >
                  {d.absences.length} {t("nav.absences").toLowerCase()}
                </div>
              )}

              {d.jobs.map((j) => {
                const { top, height } = slotFor(j, d.key);
                const group = orderedGroup(d, j);
                const idx = group.indexOf(j.id);
                const size = group.length;
                const isFront = idx === 0;
                return (
                  <button
                    key={j.id}
                    {...dragProps(j)}
                    data-testid={`week-job-${d.key}-${j.id}`}
                    onClick={() => {
                      if (isFront) {
                        onOpenJob(j);
                        return;
                      }
                      // Byt plats med den som ligger längst fram
                      setOrder((prev) => {
                        const base = prev[d.key] ? [...prev[d.key]] : d.jobs.map((x) => x.id);
                        const a = base.indexOf(j.id);
                        const b = base.indexOf(group[0]);
                        [base[a], base[b]] = [base[b], base[a]];
                        return { ...prev, [d.key]: base };
                      });
                      onSelect(d.key);
                    }}
                    onDoubleClick={() => onOpenJob(j)}
                    title={`${j.title} ${j.start?.slice(11, 16) || ""} · ${t("cal.openHint")}`}
                    style={{
                      top,
                      height,
                      // Alla ordrar får samma smala bredd oavsett dag och antal
                      left: `${(idx * 100) / Math.max(size, 3)}%`,
                      width: `${100 / Math.max(size, 3)}%`,
                    }}
                    className={`absolute overflow-hidden rounded-sm border border-slate-200 bg-white px-1 py-0.5 text-left text-[10px] shadow-sm transition-colors duration-200 hover:border-blue-400 ${
                      typeBar[j.job_type] || typeBar.field
                    } ${dragJob?.id === j.id ? "opacity-40" : ""}`}
                  >
                    <span className="block truncate leading-tight">
                      <span className="font-semibold text-slate-700">
                        {j.start?.slice(11, 16)}
                        {j.end ? `–${j.end.slice(11, 16)}` : ""}
                      </span>
                    </span>
                    <span className="block truncate font-medium leading-tight text-slate-900">{j.title}</span>
                    <span className="block truncate leading-tight text-slate-500">
                      {(j.assigned_to || []).map((uid) => staffName(uid)).join(", ") || t("cal.unassigned")}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeekSchedule;
